import { useState } from "react"
import useStore from "../../../store"
import FormInput from "../../common/FormInput"
import SelectDropdown from "../../common/SelectDropdown"
import { HappyButton, NeutralButton } from "../../common/Buttons"
import Spinner from "../../common/Spinner"
import US_STATES from "../../../utilities/statesList"

const PLACE_TYPES = [
  { id: 1, name: 'Dealer Lot' },
  { id: 2, name: 'Dealer Home' },
  { id: 3, name: 'Repair Facility' },
  { id: 4, name: 'Auction' },
  { id: 5, name: 'Other' },
  { id: 6, name: 'Mailing' },
  { id: 7, name: 'Additional Phone' },
]

const INITIAL_VALUES = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
}

export default function DealerPlaceForm({ dealerId, onClose }) {
  const createDealerPlace = useStore((state) => state.createDealerPlace)
  const isCreating = useStore((state) => state.isCreatingDealerPlace)
  const [selectedPlaceType, setSelectedPlaceType] = useState(null)
  const [inputValues, setInputValues] = useState(INITIAL_VALUES)
  const [errors, setErrors] = useState({})

  const handleChange = (field) => (e) => {
    setInputValues({ ...inputValues, [field]: e.target.value })
    if (errors[field]) setErrors({ ...errors, [field]: false })
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedPlaceType) newErrors.place_type = true
    if (!inputValues.address1.trim()) newErrors.address1 = true
    if (!inputValues.city.trim()) newErrors.city = true
    if (!inputValues.state.trim()) newErrors.state = true
    if (!inputValues.zip.trim()) newErrors.zip = true
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    await createDealerPlace(dealerId, {
      place_type: selectedPlaceType.id,
      ...inputValues,
    })
    onClose()
  }

  return (
    <div className="flex flex-col gap-3 p-2">
      <SelectDropdown
        label="Place Type"
        options={PLACE_TYPES}
        selectedItem={selectedPlaceType}
        setSelectedItem={(item) => {
          setSelectedPlaceType(item)
          if (errors.place_type) setErrors({ ...errors, place_type: false })
        }}
        displaySelectedItem={(item) => item.name}
        defaultText="Select a place type"
        isErrored={!!errors.place_type}
        testId="place-type-select"
      />

      <FormInput
        title="Address 1"
        value={inputValues.address1}
        onChange={handleChange('address1')}
        isErrored={!!errors.address1}
      />
      <FormInput
        title="Address 2"
        value={inputValues.address2}
        onChange={handleChange('address2')}
      />

      <div className="flex gap-3">
        <div className="flex-1">
          <FormInput
            title="City"
            value={inputValues.city}
            onChange={handleChange('city')}
            isErrored={!!errors.city}
          />
        </div>
        <div className="w-24 my-1">
          <SelectDropdown
            label="State"
            options={US_STATES}
            selectedItem={US_STATES.find((state) => state.id === inputValues.state) || null}
            setSelectedItem={(item) => {
              setInputValues({ ...inputValues, state: item?.id || '' })
              if (errors.state) setErrors({ ...errors, state: false })
            }}
            displaySelectedItem={(item) => item.id}
            defaultText="--"
            isErrored={!!errors.state}
            testId="state-select"
          />
        </div>
        <div className="w-28">
          <FormInput
            title="Zip"
            value={inputValues.zip}
            onChange={handleChange('zip')}
            isErrored={!!errors.zip}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <FormInput
            title="Phone"
            value={inputValues.phone}
            onChange={handleChange('phone')}
          />
        </div>
        <div className="flex-1">
          <FormInput
            title="Fax"
            value={inputValues.fax}
            onChange={handleChange('fax')}
          />
        </div>
      </div>

      <div className="flex justify-evenly items-center mt-2">
        {isCreating ? (
          <Spinner color="green" />
        ) : (
          <>
            <HappyButton
              text="Create Place"
              onClick={handleSubmit}
              style="my-4 w-30 justify-items-center"
            />
            <NeutralButton
              text="Cancel"
              onClick={onClose}
              style="my-4 w-30 justify-items-center"
            />
          </>
        )}
      </div>
    </div>
  )
}
